/**
 * TourProvider — React context that manages the guided tour lifecycle.
 *
 * Two independent, page-anchored tours (2026-09-05: the Onboarding Tour and
 * Workflow Tour were retired -- both mostly re-narrated things the AI
 * conversation on the Research page already walks a new user through step
 * by step; see tourSteps.ts's own doc comment for the full reasoning):
 * 1. Loading Tour (job progress page) — fills the wait while a backtest runs
 * 2. Results Tour (ResultsVisualization) — analytics sections
 *
 * Each is self-contained; neither chains into the other or into anything
 * else.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { driver, type Driver, type PopoverDOM } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour-theme.css';
import { getAvailableTourSteps, getAvailableLoadingSteps } from './tourSteps';
import {
  trackTourStarted,
  trackTourStepView,
  trackTourCompleted,
  trackTourDismissed,
} from '@/lib/analytics';
import type { DriveStep } from 'driver.js';

type TourTrigger = 'auto' | 'manual';

// Derive a stable id for telemetry from a step: prefer its anchor selector,
// fall back to the popover title (intro/centered steps have no element).
function tourStepId(step: DriveStep): string {
  if (typeof step.element === 'string') return step.element;
  return step.popover?.title ?? 'intro';
}

// Build a driver.js onHighlighted hook that emits a step_view event for the
// given journey. `state.activeIndex` is the 0-based step index.
function makeStepViewHook(journey: string) {
  return (_el?: Element, step?: DriveStep, opts?: { state?: { activeIndex?: number } }) => {
    if (!step) return;
    trackTourStepView(journey, tourStepId(step), opts?.state?.activeIndex ?? 0);
  };
}

// ---------------------------------------------------------------------------
// Safe localStorage helpers
// localStorage can throw (Safari private mode, disabled cookies, quota exceeded).
// We treat any failure as if the key were absent / the write a no-op so the
// tour just runs again next session rather than crashing the provider.
// ---------------------------------------------------------------------------
const safeGetItem = (key: string): string | null => {
  try { return window.localStorage.getItem(key); } catch { return null; }
};
const safeSetItem = (key: string, value: string): void => {
  try { window.localStorage.setItem(key, value); } catch { /* storage unavailable */ }
};

// ---------------------------------------------------------------------------
// localStorage keys
// ---------------------------------------------------------------------------
const RESULTS_COMPLETED_KEY = 'results_tour_completed';
const RESULTS_DISMISSED_KEY = 'results_tour_dismissed';
const LOADING_COMPLETED_KEY = 'loading_tour_completed';
const LOADING_DISMISSED_KEY = 'loading_tour_dismissed';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------
interface TourContextValue {
  // Results tour (ResultsVisualization)
  startResultsTour: (fullTour?: boolean, trigger?: 'auto' | 'manual') => void;
  stopResultsTour: () => void;
  hasCompletedTour: boolean;
  hasDismissedTour: boolean;
  markTourComplete: () => void;

  // Loading tour (progress screen)
  startLoadingTour: (trigger?: 'auto' | 'manual') => void;
  hasCompletedLoadingTour: boolean;
  hasDismissedLoadingTour: boolean;

  // General
  isTourActive: boolean;
  stopAnyTour: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within <TourProvider>');
  return ctx;
}

// ---------------------------------------------------------------------------
// Shared driver.js options
// ---------------------------------------------------------------------------
// Accessibility: announce each step to screen readers and move keyboard focus
// into the popover so SR/keyboard users aren't left on the obscured page.
// Shared between the config-level hook and step-level onPopoverRender hooks
// (step-level hooks REPLACE the config-level one in driver.js, so any step
// that defines its own must call this explicitly).
function applyPopoverA11y(popover: PopoverDOM): void {
  const { wrapper, title, description, nextButton } = popover;
  if (wrapper) {
    wrapper.setAttribute('role', 'dialog');
    // polite live region => content is re-announced as steps advance
    wrapper.setAttribute('aria-live', 'polite');
    if (title) {
      if (!title.id) title.id = 'tour-popover-title';
      wrapper.setAttribute('aria-labelledby', title.id);
    }
    if (description) {
      if (!description.id) description.id = 'tour-popover-desc';
      wrapper.setAttribute('aria-describedby', description.id);
    }
  }
  nextButton?.focus();
}

const DRIVER_COMMON = {
  showProgress: true,
  animate: true,
  smoothScroll: true,
  stagePadding: 8,
  stageRadius: 10,
  popoverOffset: 14,
  allowClose: true,
  overlayOpacity: 0.65,
  nextBtnText: 'Next',
  prevBtnText: 'Back',
  doneBtnText: 'Done',
  progressText: 'Step {{current}} of {{total}}',
  onPopoverRender: applyPopoverA11y,
} as const;

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const driverRef = useRef<Driver | null>(null);
  const [isTourActive, setIsTourActive] = useState(false);

  // ---- Results (ResultsVisualization) ----
  const [hasCompletedTour, setHasCompletedTour] = useState(() =>
    safeGetItem(RESULTS_COMPLETED_KEY) === 'true',
  );
  const [hasDismissedTour, setHasDismissedTour] = useState(() =>
    safeGetItem(RESULTS_DISMISSED_KEY) === 'true',
  );

  // ---- Loading (Progress screen) ----
  const [hasCompletedLoadingTour, setHasCompletedLoadingTour] = useState(() =>
    safeGetItem(LOADING_COMPLETED_KEY) === 'true',
  );
  const [hasDismissedLoadingTour, setHasDismissedLoadingTour] = useState(() =>
    safeGetItem(LOADING_DISMISSED_KEY) === 'true',
  );

  // ---- Shared helpers ----
  const stopAnyTour = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
    setIsTourActive(false);
  }, []);

  // ---- Results Tour ----
  const markTourComplete = useCallback(() => {
    safeSetItem(RESULTS_COMPLETED_KEY, 'true');
    setHasCompletedTour(true);
  }, []);

  const stopResultsTour = useCallback(() => {
    stopAnyTour();
  }, [stopAnyTour]);

  const startResultsTour = useCallback(
    (fullTour = false, trigger: TourTrigger = 'manual') => {
      stopAnyTour();
      const steps = getAvailableTourSteps(fullTour);
      if (steps.length === 0) return;

      const d = driver({
        ...DRIVER_COMMON,
        steps,
        onHighlighted: makeStepViewHook('results'),
        onDestroyStarted: () => {
          if (d.isLastStep()) {
            trackTourCompleted('results');
            markTourComplete();
          } else {
            trackTourDismissed('results');
            safeSetItem(RESULTS_DISMISSED_KEY, 'true');
            setHasDismissedTour(true);
          }
          d.destroy();
        },
        onDestroyed: () => {
          setIsTourActive(false);
          driverRef.current = null;
        },
      });

      driverRef.current = d;
      setIsTourActive(true);
      trackTourStarted('results', trigger);
      requestAnimationFrame(() => d.drive());
    },
    [stopAnyTour, markTourComplete],
  );

  // ---- Loading Tour ----
  const startLoadingTour = useCallback((trigger: TourTrigger = 'auto') => {
    stopAnyTour();
    const steps = getAvailableLoadingSteps();
    if (steps.length === 0) return;

    const d = driver({
      ...DRIVER_COMMON,
      steps,
      onHighlighted: makeStepViewHook('loading'),
      onDestroyStarted: () => {
        if (d.isLastStep()) {
          trackTourCompleted('loading');
          safeSetItem(LOADING_COMPLETED_KEY, 'true');
          setHasCompletedLoadingTour(true);
        } else {
          trackTourDismissed('loading');
          safeSetItem(LOADING_DISMISSED_KEY, 'true');
          setHasDismissedLoadingTour(true);
        }
        d.destroy();
      },
      onDestroyed: () => {
        setIsTourActive(false);
        driverRef.current = null;
      },
    });

    driverRef.current = d;
    setIsTourActive(true);
    trackTourStarted('loading', trigger);
    requestAnimationFrame(() => d.drive());
  }, [stopAnyTour]);

  const value: TourContextValue = {
    startResultsTour,
    stopResultsTour,
    hasCompletedTour,
    hasDismissedTour,
    markTourComplete,
    startLoadingTour,
    hasCompletedLoadingTour,
    hasDismissedLoadingTour,
    isTourActive,
    stopAnyTour,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export default TourProvider;
